import { type RouteLocationNormalized } from "vue-router";
import { type RouterAuthService } from "./types";
import { type TermsAndConditionResponse } from "@/views/TermsAndConditionView";
import { type CustomersApiResponse, type CustomerErrorResponse } from "@/views/UnauthorizedView";
import { useUserStore } from "@/store";
import { storeToRefs } from "pinia";
import { defaultApi } from "@/api";
import { bypassAuth, createMockUser, generalLogic } from "@/global";
import { FEATURE_NAMES, logInfo, logError } from "@/loggers";
import { hasAnyProductEnabled } from "@/utils/productUtils";

// Type guard to check if response data is an error
const isCustomerErrorResponse = (data: any): data is CustomerErrorResponse => {
  return data && typeof data === "object" && "errorMessage" in data;
};

type CustomerApiErrorShape = {
  statusCode: number;
  errorMessage: string;
};

const isCustomerApiError = (error: unknown): error is CustomerApiErrorShape => {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const candidate = error as Record<string, unknown>;
  return typeof candidate.statusCode === "number" && typeof candidate.errorMessage === "string";
};

const { decodeJwt } = generalLogic;

let lastLoggedPath = "";

type PersistCustomerFn = (customer: unknown) => void;

type GuardResult = true | false | { name: string; query?: Record<string, boolean> };

/**
 * Kicks off the download of the matched route's async component(s) immediately,
 * without waiting for it to be resolved by the router. This is a fire-and-forget prefetch, and does not affect or delay the router guard.
 */
const prefetchMatchedRouteComponents = (to: RouteLocationNormalized): void => {
  to.matched.forEach((record) => {
    Object.values(record.components ?? {}).forEach((component) => {
      if (typeof component === "function") {
        (component as () => Promise<unknown>)().catch((error) => {
          logError(FEATURE_NAMES.MVS, `Prefetch of route component failed for ${record.path}: ${error}`);
        });
      }
    });
  });
};

// Type guard to check if response data is an error
const handleExistingUser = (
  to: RouteLocationNormalized,
  user: { value: { customer?: unknown; preferences?: { termsAccepted?: boolean } } | null }
): GuardResult | undefined => {
  if (!user.value) return undefined;
  if (to.name === "Unauthorized" && user.value.customer && user.value.preferences?.termsAccepted) {
    logInfo(FEATURE_NAMES.MVS, `User already authenticated with permissions - redirecting from Unauthorized to app`);
    return { name: "Dashboard" };
  }
  if (to.path !== lastLoggedPath) {
    logInfo(FEATURE_NAMES.MVS, `User already authenticated - allowing access to ${to.path}`);
    lastLoggedPath = to.path;
  }
  return true;
};

const handleTermsAndRedirect = (
  termsAccepted: boolean,
  digitalMarketingAccepted: boolean,
  to: RouteLocationNormalized
): GuardResult => {
  if (!termsAccepted) {
    logInfo(FEATURE_NAMES.MVS, `Terms not accepted - redirecting to terms and conditions page`);
    return { name: "TermsAndConditions", query: { termsAccepted, digitalMarketingAccepted } };
  }
  if (to.name === "Unauthorized") {
    logInfo(FEATURE_NAMES.MVS, `Terms accepted and customer loaded - redirecting from Unauthorized to app`);
    return { name: "Dashboard" };
  }
  logInfo(FEATURE_NAMES.MVS, `Terms accepted - allowing access to ${to.path}`);
  return true;
};

const getCustomerAndPreferences = async (persistCustomer?: PersistCustomerFn) => {
  const customerPromise: Promise<CustomersApiResponse> = defaultApi.apiUnversioned.customers.getOne();
  const preferencesPromise = defaultApi.apiVersion1.userPreferences.getAll<TermsAndConditionResponse>({
    headers: { "X-Suppress-Auth-Redirect": 1 }
  });

  const [customerResult, preferencesResult] = await Promise.allSettled([customerPromise, preferencesPromise]);

  if (customerResult.status === "rejected") {
    logError(FEATURE_NAMES.MVS, `Customer API call rejected: ${customerResult.reason}`);
    throw customerResult.reason ?? new Error("Customer API failed");
  }

  const customerResponse = customerResult.value;

  if (customerResponse.status === 403) {
    const errorMessage = isCustomerErrorResponse(customerResponse.data)
      ? customerResponse.data.errorMessage
      : "insufficient permissions";

    logError(FEATURE_NAMES.MVS, `Customer API returned 403: ${errorMessage}`);

    const error = new Error(errorMessage);
    Object.assign(error, {
      statusCode: customerResponse.status,
      errorMessage: isCustomerErrorResponse(customerResponse.data)
        ? customerResponse.data.errorMessage
        : "User does not have necessary permissions"
    });
    throw error;
  }

  const customer = customerResponse.data;

  if (isCustomerErrorResponse(customer)) {
    logError(FEATURE_NAMES.MVS, `Received error response instead of customer data: ${customer.errorMessage}`);
    throw new Error(customer.errorMessage);
  }

  // Persist customer as soon as we have it, so Unauthorized page can still show account permissions
  // even if the preferences/consent API fails.
  persistCustomer?.(customer);

  let preferences: TermsAndConditionResponse["data"];
  if (preferencesResult.status === "fulfilled") {
    preferences = preferencesResult.value.data;
  } else {
    throw preferencesResult.reason ?? new Error("User preferences API failed");
  }

  // Check if any product is enabled for the customer
  if (!hasAnyProductEnabled(customer)) {
    logError(FEATURE_NAMES.MVS, `No products enabled for customer`);
    throw new Error("No products enabled for customer");
  }

  logInfo(FEATURE_NAMES.MVS, `Successfully fetched customer details and user preferences`);
  return { customer, preferences };
};

export const getAuthRouterGuard = (authService: RouterAuthService) => {
  return async (to: RouteLocationNormalized): Promise<GuardResult> => {
    if (!to.meta.requiresAuth || import.meta.env.VITE_E2E) {
      return true;
    }

    /**
     * Start downloading the destination route's JS chunk right now, in parallel
     * with the customer/preferences calls below, instead of waiting for this
     * whole guard to resolve first (Vue Router's default behavior). This is a
     * fire-and-forget prefetch - it does not affect or delay this guard.
     */
    prefetchMatchedRouteComponents(to);

    const userStore = useUserStore();
    const { setUser, setCustomerApiError } = userStore;
    const { user } = storeToRefs(userStore);

    const existingResult = handleExistingUser(to, user);
    if (existingResult !== undefined) {
      return existingResult;
    }

    logInfo(FEATURE_NAMES.MVS, `No user in store - checking authentication with IAM`);
    const authenticatedUser = await authService.getAuthenticatedUser();

    if (bypassAuth) {
      console.log("Auth router guard: Using mock user token");
      setUser({ ...createMockUser(), ...authenticatedUser });
      return true;
    }

    const userToken = decodeJwt(authenticatedUser?.access_token);
    if (!authenticatedUser || !userToken) {
      logInfo(FEATURE_NAMES.MVS, `No valid authenticated user or token found - redirecting to IAM login`);
      await authService.login();
      return false;
    }

    logInfo(
      FEATURE_NAMES.MVS,
      `Valid user token found - customer code: ${userToken.customer_code}, username: ${userToken.sub ?? "N/A"} / ${userToken.subname ?? "N/A"}, roles: ${JSON.stringify(userToken.roles || [])}`
    );

    const allUserRoles = userToken?.roles ?? [];
    const hasIsceRole = allUserRoles.filter((role: string) => role.startsWith("ISCE")) ?? [];
    setUser({
      ...authenticatedUser,
      userToken,
      roles: hasIsceRole ? allUserRoles : []
    });

    try {
      logInfo(FEATURE_NAMES.MVS, `Fetching customer and preferences for authenticated user`);
      const { customer, preferences } = await getCustomerAndPreferences((customerData) => {
        setUser(user.value ? { ...user.value, customer: customerData } : { customer: customerData });
      });
      setUser(user.value ? { ...user.value, customer, preferences } : { customer, preferences });
      userStore.setPreferences(preferences);
      return handleTermsAndRedirect(preferences.termsAccepted, preferences.digitalMarketingAccepted, to);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logError(
        FEATURE_NAMES.MVS,
        `Failed to fetch customer/preferences: ${errorMessage} - redirecting to unauthorized`
      );
      if (isCustomerApiError(error)) {
        setCustomerApiError({ statusCode: error.statusCode, errorMessage: error.errorMessage });
      }
      return { name: "Unauthorized" };
    }
  };
};
